import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { filter, take } from 'rxjs/operators';
import { AtlasConfigService, PlatformAccessService } from '@galaxy/atlas/core';
import { AccountIdLoggedInCanActivate } from '../../guards';
import { AppInsufficientPermissionRouteParams } from '../../core/routeparams';
import { VDC_APP_ID } from '../../../globals';

@Component({
  selector: 'app-insufficient-permission',
  templateUrl: './app-insufficient-permission.component.html',
  styleUrls: ['./app-insufficient-permission.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AppInsufficientPermissionComponent implements OnInit {
  protected accountGroupId: string;
  protected signinLink: string;
  protected entitlementId: string | null = null;
  protected partnerId: string | null = null;
  protected returnUrl: string | null = null;

  private readonly route = inject(ActivatedRoute);
  private readonly atlasConfigService = inject(AtlasConfigService);
  private readonly platformAccessService = inject(PlatformAccessService);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.accountGroupId = (this.route.snapshot.params as AppInsufficientPermissionRouteParams).accountGroupId;

    if (this.accountGroupId) {
      this.signinLink = AccountIdLoggedInCanActivate.buildLoginUrl(this.accountGroupId, `/entry/${this.accountGroupId}`);
    }

    const queryParams = this.route.snapshot.queryParams;
    this.entitlementId = queryParams['entitlementId'] || null;
    this.partnerId = queryParams['partnerId'] || null;
    this.returnUrl = queryParams['returnUrl'] || null;

    if (this.entitlementId && this.partnerId) {
      this.atlasConfigService.initialize(
        VDC_APP_ID,
        this.partnerId,
        '',
        this.accountGroupId,
        VDC_APP_ID,
        '',
        this.entitlementId,
      );

      this.platformAccessService.accessCheckResult$
        .pipe(
          filter((granted) => granted),
          take(1),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(() => this.redirectAfterGrant());

      this.platformAccessService.checkPlatformAccess();
    }
  }

  protected get isEntitlementRequestable(): boolean {
    return !!this.entitlementId && !!this.partnerId;
  }

  protected redirectAfterGrant(): void {
    this.navigateTo(this.resolveRedirectTarget(this.returnUrl, this.accountGroupId));
  }

  protected navigateTo(url: string): void {
    window.location.href = url;
  }

  protected resolveRedirectTarget(returnUrl: string | null, accountGroupId: string): string {
    if (returnUrl) {
      try {
        if (new URL(returnUrl).protocol === 'https:') {
          return returnUrl;
        }
      } catch {
        // An unparseable returnUrl falls through to the safe dashboard default.
      }
    }
    return `/entry/${accountGroupId}`;
  }
}
